/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import {
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISortByViewProps
} from './dlvry-search-result-container';
import { ISearchResultModalViewProps } from '@msdyn365-commerce-modules/search-result-container';
import { getTelemetryObject, Module, Node } from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import classnames from 'classnames';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    IDlvrySearchResultContainerProps
} from './dlvry-search-result-container.props.autogenerated';
import {
    IProductListViewModeProps,
    ProductListViewMode,
    ProductListViewModeComponent
} from '../../components/product-list-component/components/product-list-view-mode';
import { ProductComponent } from '../../themes/dlvry-adventureworks/views/components/product.component';
import PQueue from 'p-queue';
import { IProductInventoryInformation } from '../../components/common/product-inventory-utils';
import { onBuyHasAccessValidator } from '../../shared-utils/attributes/on-buy-access-validator';
import { getCustomerAgreements } from '../../shared-utils/agreement-product-utils';

/**
 * DescriptionInterval.
 */
enum DescriptionInterval {
    productDescriptionInterval = 14,
    productDescriptionIntervalRemainder0 = 0,
    productDescriptionIntervalRemainder9 = 9
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProductCards = (props: ISearchResultContainerViewProps & IDlvrySearchResultContainerProps<{}>): JSX.Element | null => {
    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            {props.products}
        </Node>
    );
};

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnabledProductDescription - The flag for showing Product desription tile.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: ISearchResultContainerViewProps & IDlvrySearchResultContainerProps<{}>,
    cartQueue: PQueue,
    product: ProductSearchResult,
    index: number,
    productInventoryStatus: IProductInventoryInformation[] | undefined,
    isEnabledProductDescription?: boolean
): JSX.Element | null => {
    const { config, context, resources, productsDimensionAvailabilities } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnabledProductDescription
        ? 'ms-product-search-result__item product__description'
        : 'ms-product-search-result__item';
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const productComparisonButton = ArrayExtensions.hasElements(props.slots.productComparisonButton)
        ? props.slots.productComparisonButton[0]
        : null;

    return (
        <li className={classname} key={index}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={config.imageSettings}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                depositSuffixText={resources.depositSuffixText}
                priceNotLoggedInText={resources.priceNotLoggedInText}
                allowBack={isAllowBack}
                id={props.id}
                key={product.RecordId}
                typeName={props.typeName}
                data={{
                    cart: props.data.cart.result,
                    product,
                    simpleProduct: props.simpleProducts.find(x => x.RecordId === product.RecordId),
                    customerInformation: props.data.customerInformation.result,
                    customerAgreements: getCustomerAgreements(context, props.data.orgInformation.result?.Attributes),
                    unitConversions: props.unitConversions?.filter(x => x.ProductRecordId === product.RecordId),
                    brandSchemes: props.brandSchemes
                }}
                cartQueue={cartQueue}
                buyAlwaysAdd
                retrieveOrderTemplates={props.retrieveOrderTemplates}
                onUpdateOrderTemplates={props.onUpdateOrderTemplate}
                quickViewButton={quickviewslot}
                productComparisonButton={productComparisonButton}
                isEnabledProductDescription={isEnabledProductDescription}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                inventoryStatus={productInventoryStatus?.find(x => x.ProductAvailableQuantity.ProductId === product.RecordId)}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                priceResources={priceResources}
                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                addToCartText={resources.addToCartButtonText}
                updateCartText={resources.updateCartText}
                removeFromCartText={resources.removeFromCartText}
                outOfStockText={resources.outOfStockText}
                priceErrorText={resources.priceErrorText}
                toastStrings={{
                    errorHeader: resources.toastErrorHeader,
                    productsAddedToCart: resources.productsAddedToCart,
                    productsRemovedFromCart: resources.productsRemovedFromCart,
                    productsUpdatedInCart: resources.productsUpdatedInCart,
                    goToCart: resources.toastGoToCart,
                    noAccessToProduct: resources.toastNoAccessToProduct
                }}
                inCartTooltipText={resources.inCartTooltipText}
                transitTooltip={resources.transitTooltip}
                agreementProductTooltip={resources.agreementProductTooltip}
                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                    (dimensionAvailability: IProductsDimensionsAvailabilities[]) =>
                        ArrayExtensions.hasElements(dimensionAvailability) &&
                        dimensionAvailability[0].masterProductId === product.MasterProductId
                )}
                addToOrderTemplateResources={{
                    addToOrderTemplateHeader: resources.addToOrderTemplateHeader,
                    noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
                    noOrderTemplatesDescription: resources.noOrderTemplatesDescription,
                    createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
                    createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
                    cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,
                    selectTemplatesText: resources.selectTemplatesText,
                    addToTemplateButtonText: resources.addToTemplateButtonText,
                    lineItemsText: resources.lineItemsText,
                    createOrderTemplateHeader: resources.createOrderTemplateHeader,
                    orderTemplateTitle: resources.orderTemplateTitle,
                    orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
                    createOrderTemplateDescription: resources.createOrderTemplateDescription,
                    defaultOrderTemplateName: resources.defaultOrderTemplateName,
                    createOrderTemplateButtonText: resources.createOrderTemplateButtonText,
                    cancelNewOrderTemplateCreationButtonText: resources.cancelNewOrderTemplateCreationButtonText,
                    viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
                    continueShoppingButtonText: resources.continueShoppingButtonText,
                    itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
                    itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
                    itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
                    priceFree: resources.priceFree,
                    originalPriceText: resources.originalPriceText,
                    currentPriceText: resources.currentPriceText,
                    duplicatedProductsHeader: resources.duplicatedProductsHeader,
                    duplicatedProductsDescription: resources.duplicatedProductsDescription,
                    updateQuantityButtonText: resources.updateQuantityButtonText,
                    cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText,
                    addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                    addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip
                }}
                onBuyValidate={(product) => onBuyHasAccessValidator(
                    props.context,
                    props.data.orgInformation.result,
                    product,
                    props.resources.toastNoAccessToProduct
                )}
            />
        </li>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (
    props: ISearchResultContainerViewProps & IDlvrySearchResultContainerProps<{}>,
    cartQueue: PQueue,
    productInventoryStatus: IProductInventoryInformation[] | undefined
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }

    const productDescriptionInterval: number = DescriptionInterval.productDescriptionInterval;
    const productDescriptionIntervalRemainder0: number = DescriptionInterval.productDescriptionIntervalRemainder0;
    const productDescriptionIntervalRemainder9: number = DescriptionInterval.productDescriptionIntervalRemainder9;

    if (props.viewModesComponent.viewMode === undefined) {
        // Not ready yet
        return null;
    }

    if (props.viewModesComponent.viewMode === ProductListViewMode.listView) {
        return (
            <Node {...props.ProductsContainer}>
                {props.errorMessage}
                {props.products}
            </Node>
        );
    }

    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='ms-product-search-result__grid 1'>
                {products.map((product: ProductSearchResult, index: number) =>
                    index % productDescriptionInterval === productDescriptionIntervalRemainder0 ||
                        index % productDescriptionInterval === productDescriptionIntervalRemainder9
                        ? renderProductCardsDescription(props, cartQueue, product, index, productInventoryStatus, true)
                        : renderProductCardsDescription(props, cartQueue, product, index, productInventoryStatus, false)
                )}
            </ul>
        </Node>
    );
};

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps & IDlvrySearchResultContainerProps<{}>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        isMobile,
        modalToggle,
        searchResultModal,
        choiceSummary,
        refineMenu,
        sortByOptions,
        RefineAndProductSectionContainer,
        productInventoryStatus
    } = props;

    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    const cartQueue = React.useMemo(() => new PQueue({ concurrency: 1 }), []);

    return (
        <Module {...SearchResultContainer}>
            <Node className={`${props.className}__top`}>
                <Node className={`${props.className}__top__left`}>
                    {props.totalProductCount}
                </Node>
                <Node className={`${props.className}__top__right`}>
                    {props.searchBar}
                    {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                    {renderViewModes(props.viewModesComponent)}
                </Node>
            </Node>
            {isMobile && <Node className='msc-sort-filter-wrapper'>{modalToggle}</Node>}
            {isMobile && createSearchResultModal(searchResultModal, refineMenu, choiceSummary, sortByOptions, isRecoSearchPage, props.searching)}
            <Node {...RefineAndProductSectionContainer}>
                    <Node {...ProductSectionContainer}>
                        {!isMobile && refineMenu && renderRefiner(refineMenu, choiceSummary, props.searching)}
                        {props.config.enableProdutDescription
                            ? renderProducts(props, cartQueue, productInventoryStatus)
                            : renderProductCards(props)}
                        {pagination}
                    </Node>
                </Node>
                {props.selectedProductsActions}
        </Module>
    );
};

const renderViewModes = (props: IProductListViewModeProps): JSX.Element => {
    return (
        <ProductListViewModeComponent {...props} />
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    choiceSummary: React.ReactNode,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    searching?: boolean
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, choiceSummary, sortByDropDown, isRecoSearchPage, searching),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps, searching?: boolean): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer} className={classnames(RefineMenuContainer.className, { searching })}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    choiceSummary: React.ReactNode,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    searching?: boolean
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {},
            renderSort(sortByDropDown, isRecoSearchPage),
            choiceSummary,
            renderRefinerMobile(refineMenu, searching)
        );
    }
    return null;
};

/**
 * Render Refiner.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefiner = (props: IRefineMenuViewProps, choiceSummary: React.ReactNode, searching?: boolean): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    return (
        <Node {...RefinerSectionContainer}>
            {choiceSummary}
            <Node {...RefineMenuContainer} className={classnames(RefineMenuContainer.className, { searching })}>
                {refiners?.map((submenu, index) => (
                    <React.Fragment key={index}>{submenu}</React.Fragment>
                ))}
            </Node>
        </Node>
    );
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

export default SearchResultContainerView;
